// engine/GameLoop.ts — fixed-order, fixed-timestep tick driver.
//
// Holds an ordered list of Systems and advances the world one tick at a time,
// running each system exactly once per tick in registration order. The driver
// itself allocates nothing per tick (plain indexed loop, no iterators/closures).

import type { System, Tick } from '../types.ts';

export class GameLoop {
  private readonly systems: System[] = [];
  private currentTick: Tick = 0;

  /** Register a system. Order of registration == per-tick execution order. */
  add(system: System): this {
    this.systems.push(system);
    return this;
  }

  /** Ticks elapsed so far. */
  get tick(): Tick {
    return this.currentTick;
  }

  /** Number of registered systems. */
  get systemCount(): number {
    return this.systems.length;
  }

  /**
   * Advance the simulation by `n` ticks (default 1). Each tick runs every
   * system once, in order, then increments the tick counter. Allocation-free.
   */
  step(n = 1): void {
    const systems = this.systems;
    const count = systems.length;
    for (let k = 0; k < n; k++) {
      const t = this.currentTick;
      for (let i = 0; i < count; i++) {
        (systems[i] as System).update(t);
      }
      this.currentTick = t + 1;
    }
  }
}
